// Sprint 10 fix — Equipment Health dashboard (full redesign).
//
// Layout:
//   * Top summary bar — 4 stat cards (sites, NVRs online, HDD health,
//     cameras active).
//   * One full-width card per site with NVR identity (left col) and
//     storage breakdown + recharts sparkline (right col), followed by
//     a footer strip of per-camera status badges.
//
// State sources:
//   /api/v1/sites             — list of sites with NVR identity fields
//                               (nvr_make/model/serial/firmware/configured).
//   /api/v1/cameras           — list (used for per-site camera badges
//                               + the camera_active stat).
//   /api/v1/sites/{id}/health/history — last N snapshots for the
//                               sparkline and the rendered "Last polled"
//                               text. Polled at 5min interval matching
//                               the server-side cadence.
//   POST .../health/poll      — bound to the "Poll Now" + "Test Connection"
//                               buttons.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BuildingIcon,
  HardDriveIcon,
  RefreshCwIcon,
  ServerIcon,
  SettingsIcon,
  VideoIcon,
} from "lucide-react";

import { useQueryClient } from "@tanstack/react-query";

import { useCameras } from "@/api/cameras";
import { useNvrHealthList, type NvrHealthItem } from "@/api/health";
import {
  useSites,
  usePollSiteHealth,
  useSiteHealthHistory,
  type NVRHealthSnapshotT,
} from "@/api/sites";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { cn } from "@/lib/utils";
import type { Camera, Site } from "@/types";


function formatGB(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1024) return `${(v / 1024).toFixed(1)} TB`;
  return `${v.toFixed(0)} GB`;
}

function formatUptime(sec: number | null | undefined): string {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}


// ---------------------------------------------------------------------------
// Top stat cards
// ---------------------------------------------------------------------------
function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = "default",
}: {
  icon: typeof BuildingIcon;
  label: string;
  value: string | number;
  sub?: string;
  tone?: "default" | "ok" | "warn" | "error";
}) {
  const toneClass = {
    default: "text-foreground",
    ok: "text-emerald-600",
    warn: "text-yellow-600",
    error: "text-red-600",
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3 p-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className={cn("mt-1 text-2xl font-semibold", toneClass)}>{value}</p>
          {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className="rounded-md bg-primary/5 p-2 text-primary">
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Sparkline — recharts AreaChart, no axes
// ---------------------------------------------------------------------------
function StorageSparkline({ series }: { series: NVRHealthSnapshotT[] }) {
  const data = useMemo(
    () =>
      series
        .slice()
        .reverse()
        .filter((r) => r.hdd_used_gb != null)
        .map((r, i) => ({ i, used: r.hdd_used_gb ?? 0 })),
    [series],
  );
  if (data.length === 0) {
    return (
      <div className="flex h-16 items-center justify-center text-[11px] text-muted-foreground">
        No history yet — click Poll Now.
      </div>
    );
  }
  return (
    <div className="h-16 w-full">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="sview-storage-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ff6d00" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#ff6d00" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis dataKey="i" hide />
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Tooltip
            content={({ active, payload }) =>
              active && payload && payload.length ? (
                <div className="rounded bg-primary px-2 py-1 text-[10px] text-white shadow">
                  {(payload[0].value as number).toFixed(1)} GB
                </div>
              ) : null
            }
          />
          <Area
            type="monotone"
            dataKey="used"
            stroke="#ff6d00"
            strokeWidth={1.5}
            fill="url(#sview-storage-fill)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Per-site card
// ---------------------------------------------------------------------------
function SiteHealthCard({
  site,
  cameras,
}: {
  site: Site;
  cameras: Camera[];
}) {
  const qc = useQueryClient();
  const { data: history } = useSiteHealthHistory(site.id);
  const poll = usePollSiteHealth();
  // FIX C — Poll Now is async: we enqueue, then refresh /history every
  // 3s until we see a snapshot newer than the click timestamp, or we
  // hit the 30s ceiling. The history hook already feeds the card.
  const [waitingSince, setWaitingSince] = useState<number | null>(null);
  // FE BUG 1 — the agent-PUSH NVR health (/api/v1/health/nvr, the same source the
  // Health tab reads) is the source of truth; match this site's NVR by IP. The
  // ISAPI history poll is only a fallback. This makes Equipment Health AGREE with
  // the Health tab instead of showing "Never polled / not configured".
  const { data: nvrPush } = useNvrHealthList();
  const pushNvr: NvrHealthItem | undefined = (nvrPush ?? []).find(
    (n) => n.ip_address != null && site.nvr_host != null && n.ip_address === site.nvr_host,
  );
  const pushLatest: NVRHealthSnapshotT | undefined = pushNvr
    ? {
        id: pushNvr.agent_id ?? "push",
        site_id: site.id,
        camera_id: null,
        checked_at: (pushNvr.checked_at ?? new Date().toISOString()) as string,
        hdd_count: pushNvr.hdd_count,
        hdd_total_gb: pushNvr.hdd_total_gb,
        hdd_used_gb: pushNvr.hdd_used_gb,
        hdd_status:
          pushNvr.hdd_status === "critical"
            ? "error"
            : pushNvr.hdd_status === "normal"
              ? "ok"
              : (pushNvr.hdd_status as NVRHealthSnapshotT["hdd_status"]),
        hdd_smart_status: null,
        nvr_uptime_sec: pushNvr.uptime_seconds,
        cpu_pct: null,
        mem_pct: null,
        recording_channels_active: pushNvr.recording_channel_count,
        stream_status: pushNvr.is_online ? "ready" : "offline",
        error_message: null,
      }
    : undefined;
  const usingPush = pushLatest != null;
  const latest: NVRHealthSnapshotT | undefined = pushLatest ?? history?.items?.[0];
  // Treat a site as "has NVR" when it's ISAPI-configured OR the agent is pushing.
  const hasNvr = site.nvr_configured || usingPush;
  const timeoutRef = useRef<number | null>(null);
  const sitesKey = ["sites"] as const;

  useEffect(() => {
    if (waitingSince == null) return;
    if (latest && new Date(latest.checked_at).getTime() > waitingSince) {
      setWaitingSince(null);
      return;
    }
    const tick = window.setInterval(() => {
      void qc.invalidateQueries({
        queryKey: [...sitesKey, "health-history", site.id],
      });
    }, 3_000);
    timeoutRef.current = window.setTimeout(
      () => setWaitingSince(null),
      30_000,
    );
    return () => {
      window.clearInterval(tick);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [waitingSince, latest, qc, site.id]);

  function onPollNow() {
    const ts = Date.now();
    setWaitingSince(ts);
    poll.mutate(site.id);
  }
  const isPolling = poll.isPending || waitingSince != null;

  const total = latest?.hdd_total_gb ?? null;
  const used = latest?.hdd_used_gb ?? null;
  const usedPct = total && used != null ? Math.min(100, (used / total) * 100) : null;
  const usedTone =
    usedPct == null ? "bg-muted" :
    usedPct >= 90 ? "bg-red-500" :
    usedPct >= 70 ? "bg-yellow-500" : "bg-emerald-500";

  function statusBadge(status: string | null | undefined) {
    if (status === "ok") return <Badge variant="online">OK</Badge>;
    if (status === "warning") return <Badge variant="error">Warning</Badge>;
    if (status === "error" || status === "failed")
      return <Badge variant="error">{status}</Badge>;
    return <Badge variant="offline">Unknown</Badge>;
  }

  // "Awaiting first poll" state — site has NVR configured, but no
  // history rows yet. Different visual than "not configured at all".
  const awaiting = site.nvr_configured && !latest;

  return (
    <Card className="overflow-hidden">
      {/* Header — navy bar */}
      <div className="flex items-start justify-between gap-3 bg-primary px-4 py-3 text-white">
        <div className="flex items-start gap-2">
          <BuildingIcon className="mt-0.5 h-4 w-4 text-white/70" />
          <div>
            <p className="text-sm font-semibold">{site.name}</p>
            <p className="text-[11px] text-white/60">
              {site.code ?? "—"}{site.city ? ` · ${site.city}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/60">
            {latest
              ? `Last reported ${timeAgo(latest.checked_at)}${usingPush ? " · agent" : ""}`
              : site.nvr_configured
                ? "Awaiting report"
                : "Never polled"}
          </span>
          {site.nvr_configured && (
            <Button
              size="sm"
              onClick={onPollNow}
              disabled={isPolling}
              className="h-7 bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
            >
              <RefreshCwIcon
                className={cn("mr-1 h-3 w-3", isPolling && "animate-spin")}
              />
              {isPolling ? "Polling…" : "Poll Now"}
            </Button>
          )}
        </div>
      </div>

      <CardContent className="p-0">
        {/* Body — two columns */}
        <div className="grid grid-cols-1 gap-0 md:grid-cols-2">
          {/* Left — NVR details */}
          <div className="border-b border-border p-4 md:border-b-0 md:border-r">
            <div className="mb-3 flex items-center gap-2">
              <ServerIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                NVR
              </span>
            </div>
            {hasNvr ? (
              <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
                <dt className="text-muted-foreground">Make &amp; Model</dt>
                <dd className="font-medium">
                  {site.nvr_make ?? "—"} {site.nvr_model ?? ""}
                </dd>
                <dt className="text-muted-foreground">Serial No</dt>
                <dd className="font-mono text-[11px]">{site.nvr_serial ?? "—"}</dd>
                <dt className="text-muted-foreground">Firmware</dt>
                <dd>{site.nvr_firmware ?? "—"}</dd>
                <dt className="text-muted-foreground">Uptime</dt>
                <dd>{formatUptime(latest?.nvr_uptime_sec)}</dd>
                <dt className="text-muted-foreground">Channels</dt>
                <dd>
                  {site.nvr_channels ?? "—"} total
                  {cameras.length > 0 ? ` · ${cameras.length} active` : ""}
                </dd>
                <dt className="text-muted-foreground">Recording</dt>
                <dd>
                  {latest?.recording_channels_active != null
                    ? `${latest.recording_channels_active} channels active`
                    : "—"}
                </dd>
              </dl>
            ) : (
              <div className="space-y-2 rounded border border-dashed border-border bg-muted/30 p-4 text-center">
                <p className="text-xs text-muted-foreground">NVR not configured</p>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/sites/${site.id}`}>
                    <SettingsIcon className="mr-1 h-3.5 w-3.5" />
                    Configure
                  </Link>
                </Button>
              </div>
            )}
          </div>

          {/* Right — storage */}
          <div className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <HardDriveIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Storage
              </span>
            </div>
            {!hasNvr ? (
              <p className="text-xs text-muted-foreground">
                No agent report yet; configure NVR ISAPI to populate storage metrics.
              </p>
            ) : awaiting ? (
              <div className="space-y-1 rounded border border-dashed border-border bg-muted/30 p-4 text-center text-xs text-muted-foreground">
                Awaiting first poll. Click <strong>Poll Now</strong> above.
              </div>
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {latest?.hdd_count ?? 0} HDD{(latest?.hdd_count ?? 0) === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {statusBadge(latest?.hdd_status)}
                    <span className="text-[10px] text-muted-foreground">SMART</span>
                    {statusBadge(latest?.hdd_smart_status)}
                  </div>
                </div>
                {usedPct != null && (
                  <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn("h-full transition-all", usedTone)}
                      style={{ width: `${usedPct.toFixed(1)}%` }}
                    />
                  </div>
                )}
                <div className="mb-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <div className="text-muted-foreground">Used</div>
                    <div className="font-medium">{formatGB(used)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Free</div>
                    <div className="font-medium">
                      {formatGB(total != null && used != null ? total - used : null)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Total</div>
                    <div className="font-medium">{formatGB(total)}</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Storage usage — last {history?.items?.length ?? 0} polls
                </div>
                <StorageSparkline series={history?.items ?? []} />
              </>
            )}
            {latest?.error_message && (
              <p className="mt-2 text-[11px] text-destructive">{latest.error_message}</p>
            )}
          </div>
        </div>

        {/* Footer — per-camera mini badges */}
        {cameras.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-t border-border bg-muted/30 px-4 py-2 text-xs">
            <span className="mr-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              {cameras.length} cameras
            </span>
            {cameras.map((c) => {
              const live = c.status === "online";
              return (
                <span
                  key={c.id}
                  className={cn(
                    "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]",
                    live
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-border bg-background text-muted-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      live ? "bg-emerald-500" : "bg-muted-foreground/40",
                    )}
                  />
                  {c.name}
                </span>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function EquipmentHealthPage() {
  const {
    data: sitesData,
    isLoading: sitesLoading,
    isError: sitesError,
    error: sitesErrorObj,
    refetch: refetchSites,
  } = useSites();
  const { data: camsData } = useCameras({ limit: 500 });
  const { data: nvrHealth } = useNvrHealthList();

  const sites = sitesData?.items ?? [];
  const cameras = camsData?.items ?? [];

  // Group cameras by site for the footer strip.
  const camsBySite = useMemo(() => {
    const m: Record<string, Camera[]> = {};
    for (const c of cameras) {
      (m[c.site_id] ||= []).push(c);
    }
    return m;
  }, [cameras]);

  // Top-bar stat aggregates.
  const stats = useMemo(() => {
    const totalSites = sites.length;
    const nvrConfigured = sites.filter((s) => s.nvr_configured).length;
    const totalCams = cameras.length;
    const onlineCams = cameras.filter((c) => c.status === "online").length;
    // A26 — real HDD-health aggregate from the per-NVR hdd_status (normal/warning/
    // critical/unknown) instead of the hardcoded "Live".
    const nvrs = nvrHealth ?? [];
    const hddReporting = nvrs.filter((n) => n.hdd_status != null);
    const hddHealthy = hddReporting.filter((n) => n.hdd_status === "normal").length;
    const hddDegraded = hddReporting.filter(
      (n) => n.hdd_status === "warning" || n.hdd_status === "critical",
    ).length;
    return {
      totalSites,
      nvrConfigured,
      totalCams,
      onlineCams,
      // T2d — the number of NVRs actually REPORTING health, which is a
      // different population from `nvrConfigured` and always was. See the
      // stat card below.
      nvrReporting: nvrs.length,
      hddReporting: hddReporting.length,
      hddHealthy,
      hddDegraded,
    };
  }, [sites, cameras, nvrHealth]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Equipment Health</h1>
        <p className="text-sm text-muted-foreground">NVR &amp; camera infrastructure</p>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          icon={BuildingIcon}
          label="Total sites"
          value={stats.totalSites}
        />
        {/* T2d — this card used to read "NVRs configured: 0 / N" while
            /health/nvr answered 6 for the same tenant in the same session,
            and both numbers were right about different things:

              nvr_configured  a SITE holds NVR credentials for cloud polling
                              (sites.nvr_host AND sites.nvr_user)
              /health/nvr     an NVR the agent has REPORTED health for

            On an agent-push fleet the cloud never needs NVR credentials, so
            the first is legitimately near-zero and says nothing about whether
            NVRs exist. Labelling that "NVRs configured: 0" invited the reading
            "there are no NVRs". The reporting count now leads, because it is
            the one that answers "are the recorders there?", and the
            credentials count is demoted to a subtitle that says which
            question it answers. */}
        <StatCard
          icon={ServerIcon}
          label="NVRs reporting"
          value={stats.nvrReporting}
          sub={
            stats.nvrConfigured > 0
              ? `${stats.nvrConfigured} of ${stats.totalSites} sites hold NVR credentials`
              : "agent-push fleet — no cloud NVR credentials needed"
          }
          tone={stats.nvrReporting > 0 ? "ok" : "default"}
        />
        <StatCard
          icon={HardDriveIcon}
          label="HDD health"
          value={
            stats.hddReporting === 0
              ? "—"
              : `${stats.hddHealthy} / ${stats.hddReporting} OK`
          }
          sub={
            stats.hddReporting === 0
              ? "no NVR HDD data yet"
              : stats.hddDegraded > 0
                ? `${stats.hddDegraded} degraded`
                : "all healthy"
          }
          tone={
            stats.hddReporting === 0
              ? "default"
              : stats.hddDegraded > 0
                ? "warn"
                : "ok"
          }
        />
        <StatCard
          icon={VideoIcon}
          label="Cameras active"
          value={`${stats.onlineCams} / ${stats.totalCams}`}
          tone={stats.totalCams === 0 ? "default" : stats.onlineCams === stats.totalCams ? "ok" : "warn"}
        />
      </div>

      {/* Site cards.
          T2d — "No sites configured." used to render whenever `sitesData` was
          undefined, which includes every failed fetch. A 500 therefore
          asserted that the tenant has no sites. It has two. */}
      <QueryState
        isLoading={sitesLoading}
        isError={sitesError}
        error={sitesErrorObj}
        label="the site list"
        onRetry={refetchSites}
        isEmpty={sites.length === 0}
        emptyMessage="No sites configured."
      >
        <div className="space-y-3">
          {sites.map((s) => (
            <SiteHealthCard
              key={s.id}
              site={s}
              cameras={camsBySite[s.id] ?? []}
            />
          ))}
        </div>
      </QueryState>
    </div>
  );
}
